import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { Chess } from 'chess.js';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot, collection, getDocs, query, where } from 'firebase/firestore';

import { playSound, PIECE_SETS } from '../components/Board/BoardConstants';
import { BOTS } from '../utils/AppConstants';
import ArenaBoard from '../components/ArenaBoard';

// ============================================================
// 🤖 BOT BOARD — Click-based board, same style as platform
// ⚔️ BotBoard Component (Native implementation)
const BotBoard = ({ level = 1, boardTheme = 'green', pieceTheme = 'cburnett', boardWidth: propBoardWidth, user, playerColor = 'w', timeControl = 600, onExit, startFen }) => {
    const [gameId] = useState(() => `botgame_${Date.now()}_${user?.email?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '') || 'guest'}`);
    const isFinishedRef = useRef(false);

    const [game, setGame]             = useState(() => {
        try {
            if (startFen) return new Chess(startFen);
        } catch(e){}
        return new Chess();
    });
    const [fenHistory, setFenHistory] = useState(() => {
        try {
            if (startFen) {
                const c = new Chess(startFen);
                return [c.fen()];
            }
        } catch(e){}
        return [new Chess().fen()];
    });
    const [moveHistory, setMoveHistory] = useState([]);
    const [viewIndex, setViewIndex]   = useState(0);
    const [isThinking, setIsThinking] = useState(false);
    const [drawOffered, setDrawOffered] = useState(false);
    const [playerTime, setPlayerTime] = useState(timeControl);
    const [botTime, setBotTime]       = useState(timeControl);
    const [gameOverModal, setGameOverModal] = useState(null);
    const moveListRef = useRef(null);
    const gameRef     = useRef(game);

    const syncBotGameToFirestore = async (tempGame) => {
        if (!user || user.email === 'guest@hayaa.com') return;
        try {
            const botInfo = BOTS[level - 1] || BOTS[0];
            const em = botInfo.name.split(' ')[0];
            const bLabel = botInfo.name.substring(em.length).trim();
            
            await setDoc(doc(db, 'games', gameId), {
                white: playerColor === 'w' ? user.email.toLowerCase() : 'bot',
                whiteName: playerColor === 'w' ? (user.name || 'Player') : bLabel,
                black: playerColor === 'b' ? user.email.toLowerCase() : 'bot',
                blackName: playerColor === 'b' ? (user.name || 'Player') : bLabel,
                fen: tempGame.fen(),
                pgn: tempGame.pgn(),
                moves: tempGame.history(),
                turn: tempGame.turn(),
                format: 'bot',
                status: 'playing',
                createdAt: new Date().toISOString()
            });
        } catch (e) {
            console.error('Failed to sync bot game', e);
        }
    };

    useEffect(() => {
        syncBotGameToFirestore(game);
        return () => {
            if (!isFinishedRef.current && user && user.email !== 'guest@hayaa.com') {
                updateDoc(doc(db, 'games', gameId), {
                    status: 'abandoned',
                    completedAt: new Date().toISOString()
                }).catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId, user]);

    const handleGameOver = async (reason, title) => {
        isFinishedRef.current = true;
        setGameOverModal({ title, reason });
        if (!user || user.email === 'guest@hayaa.com') return;
        try {
            const botInfo = BOTS[level - 1] || BOTS[0];
            const em = botInfo.name.split(' ')[0];
            const bLabel = botInfo.name.substring(em.length).trim();
            
            const c = new Chess();
            moveHistory.forEach(m => c.move(m)); // Rebuild game linearly for perfect PGN
            
            let resultStr = '*';
            if (reason.includes('Checkmate')) resultStr = c.turn() === 'w' ? '0-1' : '1-0';
            else if (reason.includes('Draw')) resultStr = '1/2-1/2';
            else if (reason.includes('Resign')) resultStr = playerColor === 'w' ? '0-1' : '1-0';

            await setDoc(doc(db, 'games', gameId), {
                white: playerColor === 'w' ? user.email.toLowerCase() : 'bot',
                whiteName: playerColor === 'w' ? (user.name || 'Player') : bLabel,
                black: playerColor === 'b' ? user.email.toLowerCase() : 'bot',
                blackName: playerColor === 'b' ? (user.name || 'Player') : bLabel,
                fen: c.fen(),
                pgn: c.pgn(),
                moves: c.history(),
                turn: c.turn(),
                format: 'bot',
                status: 'completed',
                reason,
                result: resultStr,
                createdAt: new Date().toISOString()
            });
        } catch(e) { console.error('Failed to log game', e); }
    };


    // ── Platform-native board state ──
    const [selectedSq, setSelectedSq] = useState(null);
    const [legalDots, setLegalDots]   = useState([]);
    // ── Drag state ──
    const [dragSq, setDragSq]               = useState(null);
    const [dragPos, setDragPos]             = useState(null);
    const boardRef                          = useRef(null);
    const isDraggingRef                     = useRef(false);
    const pointerDownPosRef                 = useRef(null);
    const pointerDownSelectionRef           = useRef(null);
    const pointerDownOffsetRef              = useRef({ x: 0, y: 0 });
    const justDraggedRef                    = useRef(false);

    const boardW = Math.min(propBoardWidth || 460, 460);
    const sqSize = boardW / 8;
    const rows = playerColor === 'w' ? ['8','7','6','5','4','3','2','1'] : ['1','2','3','4','5','6','7','8'];
    const cols = playerColor === 'w' ? ['a','b','c','d','e','f','g','h'] : ['h','g','f','e','d','c','b','a'];

    useEffect(() => { gameRef.current = game; }, [game]);
    useEffect(() => {
        if (moveListRef.current) moveListRef.current.scrollTop = moveListRef.current.scrollHeight;
    }, [moveHistory]);

    // ── Countdown Timers ──
    useEffect(() => {
        if (game.isGameOver() || !!gameOverModal || isThinking) return;
        const tick = setInterval(() => {
            const currentTurn = game.turn();
            if (currentTurn === playerColor) {
                setPlayerTime(t => {
                    if (t <= 1) {
                        clearInterval(tick);
                        setTimeout(() => {
                            playSound('error');
                            handleGameOver('Timeout', '⏰ Time\'s Up! You lost.');
                        }, 50);
                        return 0;
                    }
                    return t - 1;
                });
            } else {
                setBotTime(t => {
                    if (t <= 1) {
                        clearInterval(tick);
                        setTimeout(() => {
                            playSound('collect');
                            handleGameOver('Timeout', '🏆 Victory! Bot ran out of time.');
                        }, 50);
                        return 0;
                    }
                    return t - 1;
                });
            }
        }, 1000);
        return () => clearInterval(tick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game, isThinking, playerColor, gameOverModal]);

    // 🌐 GLOBAL DRAG-AND-DROP POINTER TRACKING (LICHESS STYLE)
    useEffect(() => {
        if (!dragSq) return;

        const handleGlobalPointerMove = (e) => {
            if (!pointerDownPosRef.current) return;
            const dx = e.clientX - pointerDownPosRef.current.x;
            const dy = e.clientY - pointerDownPosRef.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                isDraggingRef.current = true;
            }
            if (boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                setDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
        };

        const handleGlobalPointerUp = (e) => {
            const targetSq = pixelToSquare(e.clientX, e.clientY);
            const wasDragMove = isDraggingRef.current && targetSq && targetSq !== dragSq;
            const draggedId = dragSq;

            if (isDraggingRef.current) {
                justDraggedRef.current = true;
                setTimeout(() => { justDraggedRef.current = false; }, 50);
            }

            // Clear drag states
            setDragSq(null);
            setDragPos(null);
            pointerDownPosRef.current = null;

            if (wasDragMove && legalDots.includes(targetSq)) {
                executeMove(draggedId, targetSq);
            }
        };

        window.addEventListener('pointermove', handleGlobalPointerMove);
        window.addEventListener('pointerup', handleGlobalPointerUp);

        return () => {
            window.removeEventListener('pointermove', handleGlobalPointerMove);
            window.removeEventListener('pointerup', handleGlobalPointerUp);
        };
    }, [dragSq, legalDots]);

    const displayFen       = fenHistory[viewIndex];
    const isViewingHistory = viewIndex < fenHistory.length - 1;
    const botColor         = playerColor === 'w' ? 'b' : 'w';
    const isPlayerTurn     = game.turn() === playerColor;


    // ──────────────────────────────────────────────────────
    // 🎯 HANDICAP-AWARE BOT ENGINE
    // ──────────────────────────────────────────────────────
    const makeBotMove = async (currentFen) => {
        if (gameRef.current.isGameOver() || !!gameOverModal || playerTime <= 0 || botTime <= 0) {
            setIsThinking(false);
            return;
        }
        setIsThinking(true);
        const botConfig = BOTS[level - 1] || BOTS[0];

        try {
            const temp     = new Chess(currentFen);
            const allMoves = temp.moves({ verbose: true });
            if (allMoves.length === 0) { setIsThinking(false); return; }

            let chosenMove  = null;
            const isRandom  = Math.random() < botConfig.randomChance;
            const avoidRC   = botConfig.avoidRecapture || 0;

            if (isRandom) {
                if (avoidRC > 0) {
                    const nonCaptures = allMoves.filter(m => !m.captured);
                    const pool = (nonCaptures.length > 0 && Math.random() < avoidRC) ? nonCaptures : allMoves;
                    chosenMove = temp.move(pool[Math.floor(Math.random() * pool.length)]);
                } else {
                    chosenMove = temp.move(allMoves[Math.floor(Math.random() * allMoves.length)]);
                }
            } else {
                let apiSuccess = false;
                try {
                    const res = await fetch('https://chess-api.com/v1', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ fen: currentFen, depth: botConfig.depth })
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.san) {
                            const engineVerbose = allMoves.find(m => m.san === data.san);
                            if (engineVerbose?.captured && avoidRC > 0 && Math.random() < avoidRC) {
                                const nonCaptures = allMoves.filter(m => !m.captured);
                                const pick = nonCaptures.length > 0 ? nonCaptures[Math.floor(Math.random() * nonCaptures.length)] : allMoves[Math.floor(Math.random() * allMoves.length)];
                                chosenMove = temp.move(pick);
                            } else {
                                chosenMove = temp.move(data.san);
                            }
                            apiSuccess = true;
                        }
                    }
                } catch (apiErr) {}

                if (!apiSuccess || !chosenMove) {
                    chosenMove = temp.move(allMoves[Math.floor(Math.random() * allMoves.length)]);
                }
            }

            if (chosenMove) {
                if (temp.isCheckmate())       playSound('notify');
                else if (chosenMove.captured) playSound('capture');
                else                          playSound('move');

                setGame(temp);
                setMoveHistory(prev => [...prev, chosenMove.san]);
                const newFen = temp.fen();
                setFenHistory(prev => [...prev, newFen]);
                setViewIndex(prev => prev + 1);

                if (temp.isGameOver()) {
                    playSound('notify');
                    handleGameOver(temp.isCheckmate() ? 'Checkmate' : 'Draw', temp.isCheckmate() ? '💀 Game Over. You Lose.' : '🤝 It\'s a Draw!');
                } else {
                    syncBotGameToFirestore(temp);
                }
            }
        } catch (e) {}

        setIsThinking(false);
    };

    // ── Click-based move execution ──
    const executeMove = (from, to) => {
        if (isThinking || isViewingHistory || !isPlayerTurn || game.isGameOver() || !!gameOverModal || playerTime <= 0 || botTime <= 0) return;
        try {
            const temp = new Chess(displayFen);
            const move = temp.move({ from, to, promotion: 'q' });
            if (move) {
                if (temp.isCheckmate())  playSound('notify');
                else if (move.captured)  playSound('capture');
                else                     playSound('move');

                setGame(temp);
                setMoveHistory(prev => [...prev, move.san]);
                const newFen = temp.fen();
                setFenHistory(prev => [...prev, newFen]);
                setViewIndex(prev => prev + 1);
                setSelectedSq(null);
                setLegalDots([]);
                setDragSq(null);
                setDragPos(null);

                if (temp.isGameOver()) {
                    playSound('collect');
                    handleGameOver(temp.isCheckmate() ? 'Checkmate' : 'Draw', temp.isCheckmate() ? '🏆 Checkmate! You Win!' : '🤝 It\'s a Draw!');
                    return;
                } else {
                    syncBotGameToFirestore(temp);
                }
                setTimeout(() => makeBotMove(temp.fen()), 350);
            }
        } catch (e) {}
    };

    // ── Pointer handlers (drag-and-drop + click) ──
    const handlePointerDown = (e, id) => {
        if (isThinking || game.isGameOver() || isViewingHistory || !!gameOverModal || playerTime <= 0 || botTime <= 0) return;
        const piece = game.get(id);
        if (!piece || piece.color !== playerColor || !isPlayerTurn) return;
        
        pointerDownSelectionRef.current = selectedSq;
        isDraggingRef.current = false;
        pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
        
        const rect = boardRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        
        const cIdx = cols.indexOf(id[0]);
        const rIdx = rows.indexOf(id[1]);
        const pieceLeft = cIdx * sqSize;
        const pieceTop = rIdx * sqSize;
        pointerDownOffsetRef.current = { x: cursorX - pieceLeft, y: cursorY - pieceTop };
        
        setDragSq(id);
        setDragPos({ x: cursorX, y: cursorY });
        
        if (selectedSq !== id) {
            setSelectedSq(id);
            setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
        }
    };

    // ── Square click handler ──
    const handleSquareClick = (id) => {
        if (justDraggedRef.current) return;
        if (isThinking || game.isGameOver() || isViewingHistory || !!gameOverModal || playerTime <= 0 || botTime <= 0) return;
        if (selectedSq) {
            if (id === selectedSq) { setSelectedSq(null); setLegalDots([]); return; }
            if (legalDots.includes(id)) {
                executeMove(selectedSq, id);
            } else {
                setSelectedSq(null);
                setLegalDots([]);
            }
        } else {
            if (!isPlayerTurn) return;
            const piece = game.get(id);
            if (piece && piece.color === playerColor) {
                setSelectedSq(id);
                setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
            }
        }
    };

    // ── Undo ──
    const handleUndo = () => {
        if (isThinking || moveHistory.length < 2) return;
        const newHistArr = moveHistory.slice(0, moveHistory.length - 2);
        const temp = new Chess();
        for (let m of newHistArr) temp.move(m);
        setGame(temp);
        const newFenHist = fenHistory.slice(0, Math.max(1, fenHistory.length - 2));
        setFenHistory(newFenHist);
        setViewIndex(newFenHist.length - 1);
        setMoveHistory(newHistArr);
        setSelectedSq(null); setLegalDots([]); setDragSq(null); setDragPos(null);
        syncBotGameToFirestore(temp);
        toast('↩ Move undone!', { duration: 1500 });
    };

    const handleOfferDraw = () => {
        if (drawOffered || gameOverModal) return;
        setDrawOffered(true);
        const willAccept = (BOTS[level - 1]?.randomChance || 0) > 0.3;
        toast(`🤝 Draw offered to ${botInfo.name.substring(botInfo.name.split(' ')[0].length).trim()}...`, { duration: 2000 });
        if (willAccept) {
            setTimeout(() => {
                playSound('notify');
                handleGameOver('Draw agreed', '🤝 It\'s a Draw!');
            }, 1800);
        } else {
            setTimeout(() => { toast('❌ Draw declined. Keep fighting!', { duration: 2000 }); setDrawOffered(false); }, 1800);
        }
    };

    const formatTime = (secs) => {
        const m = String(Math.floor(secs / 60)).padStart(2, '0');
        const s = String(secs % 60).padStart(2, '0');
        return `${m}:${s}`;
    };

    const botInfo  = BOTS[level - 1] || BOTS[0];
    const botEmoji = botInfo.name.split(' ')[0];
    const botLabel = botInfo.name.substring(botEmoji.length).trim();

    // ── Piece images: use platform PIECE_SETS (respects user's pieceTheme setting) ──
    const PIECES = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;

    // ── Board square colors ──
    const { light, dark } = (() => {
        switch (boardTheme) {
            case 'wood':  return { light: '#f0d9b5', dark: '#b58863' };
            case 'blue':  return { light: '#dee3e6', dark: '#8ca2ad' };
            default:      return { light: '#eeeed2', dark: '#769656' };
        }
    })();
    const displayUsername = user?.name || user?.email?.split('@')[0] || 'Player';

    // Build piece map from display FEN
    const pieceMap = useMemo(() => {
        const map = {};
        const rs = ['8','7','6','5','4','3','2','1'];
        const cs = ['a','b','c','d','e','f','g','h'];
        try {
            new Chess(displayFen).board().forEach((row, rIdx) => row.forEach((sq, cIdx) => {
                if (sq) map[`${cs[cIdx]}${rs[rIdx]}`] = sq.color + sq.type.toUpperCase();
            }));
        } catch (e) {}
        return map;
    }, [displayFen]);

    // ── Helper: get square id from board pixel coords ──
    const pixelToSquare = (clientX, clientY) => {
        if (!boardRef.current) return null;
        const rect = boardRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        if (x < 0 || y < 0 || x > boardW || y > boardW) return null;
        const cIdx = Math.floor(x / sqSize);
        const rIdx = Math.floor(y / sqSize);
        if (cIdx < 0 || cIdx > 7 || rIdx < 0 || rIdx > 7) return null;
        return `${cols[cIdx]}${rows[rIdx]}`;
    };

    return (
        <div style={{width: '100%', maxWidth: '960px', margin: '0 auto', display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>

            {/* ── Board Column ── */}
            <div style={{flexShrink: 0, width: boardW + 2, display: 'flex', flexDirection: 'column', gap: '8px' }}>

                {/* Bot Profile + Timer */}
                <div className="glass-panel" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderRadius: '10px' }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{fontSize: '28px', position: 'relative' }}>
                            {botEmoji}
                            {isThinking && <span style={{position: 'absolute', bottom: -2, right: -2, width: '10px', height: '10px', background: '#10b981', borderRadius: '50%', animation: 'pulse 1s infinite' }} />}
                        </div>
                        <div>
                            <div style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '15px' }}>{botLabel}</div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '12px' }}>AI Engine • Lvl {level}{isThinking ? ' • Thinking...' : ''}</div>
                        </div>
                    </div>
                    <div style={{background: game.turn() === botColor && !game.isGameOver() ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.3)', padding: '6px 18px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '22px', color: game.turn() === botColor ? '#10b981' : '#64748b', fontWeight: 'bold', border: game.turn() === botColor ? '1px solid rgba(16,185,129,0.3)' : '1px solid transparent', transition: 'all 0.3s' }}>
                        {formatTime(botTime)}
                    </div>
                </div>

                {/* Click+Drag board — same rendering as rest of platform */}
                <div ref={boardRef}
                    style={{
                        width: boardW, 
                        height: boardW, 
                        position: 'relative', 
                        borderRadius: '4px', 
                        overflow: 'hidden', 
                        boxShadow: 'var(--panel-shadow)', 
                        cursor: dragSq ? 'grabbing' : (isThinking ? 'wait' : 'default'), 
                        touchAction: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none'
                    }}>
                    
                    {/* Game Over Modal */}
                    {gameOverModal && (
                        <div style={{position: 'absolute', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                            <div className="glass-panel" style={{padding: '30px', textAlign: 'center', borderRadius: '14px', width: '80%', maxWidth: '300px', display: 'flex', flexDirection: 'column', gap: '16px', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <div style={{fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{gameOverModal.title}</div>
                                <div style={{color: 'var(--text-secondary)', fontSize: '15px' }}>{gameOverModal.reason}</div>
                                <button onClick={onExit} style={{marginTop: '10px', padding: '12px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '15px', boxShadow: '0 4px 10px rgba(16,185,129,0.3)' }}>
                                    ◀ Back to Playing Zone
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 8x8 squares */}
                    {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                        const id     = `${col}${row}`;
                        const isDark = (rIdx + cIdx) % 2 === 1;
                        const isSelected = selectedSq === id || dragSq === id;
                        const isLegal    = legalDots.includes(id);
                        const hasPiece   = !!pieceMap[id];
                        return (
                            <div key={id}
                                onClick={() => { if (!dragSq) handleSquareClick(id); }}
                                style={{position: 'absolute',
                                    left: cIdx * sqSize, top: rIdx * sqSize,
                                    width: sqSize, height: sqSize,
                                    background: isSelected ? '#f6f669' : (isDark ? dark : light),
                                    boxSizing: 'border-box',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                                }}>
                                {/* Legal move dot */}
                                {isLegal && !hasPiece && (
                                    <div style={{width: '34%', height: '34%', background: 'rgba(0,0,0,0.2)', borderRadius: '50%', pointerEvents: 'none' }} />
                                )}
                                {/* Capture ring */}
                                {isLegal && hasPiece && (
                                    <div style={{ position: 'absolute', inset: 0, border: `${Math.max(3, sqSize * 0.07)}px solid rgba(0,0,0,0.25)`, borderRadius: '50%', pointerEvents: 'none' }} />
                                )}
                            </div>
                        );
                    }))}

                    {/* Pieces rendered on top (hidden if being dragged) */}
                    {Object.entries(pieceMap).map(([id, piece]) => {
                        const cIdx = cols.indexOf(id[0]);
                        const rIdx = rows.indexOf(id[1]);
                        if (cIdx < 0 || rIdx < 0) return null;
                        
                        const isOurPiece = isPlayerTurn && piece[0] === playerColor;
                        const isBeingDragged = dragSq === id;
                        
                        return (
                            <img key={id} src={PIECES[piece]} alt={piece}
                                draggable={false}
                                onDragStart={e => e.preventDefault()}
                                onPointerDown={(e) => {
                                    if (isOurPiece) handlePointerDown(e, id);
                                }}
                                onClick={(e) => {
                                    if (justDraggedRef.current) return;
                                    e.stopPropagation(); // prevent bubbling to square div
                                    if (isOurPiece) {
                                        if (!isDraggingRef.current) {
                                            if (pointerDownSelectionRef.current === id) {
                                                setSelectedSq(null); setLegalDots([]);
                                            } else {
                                                setSelectedSq(id);
                                                setLegalDots(game.moves({ square: id, verbose: true }).map(m => m.to));
                                            }
                                        }
                                    } else {
                                        if (!dragSq) handleSquareClick(id);
                                    }
                                }}
                                style={{position: 'absolute',
                                    left: cIdx * sqSize, top: rIdx * sqSize,
                                    width: sqSize, height: sqSize,
                                    cursor: isOurPiece ? 'grab' : 'pointer',
                                    zIndex: 10,
                                    opacity: isBeingDragged ? 0.3 : 1,
                                    filter: selectedSq === id ? 'drop-shadow(0 0 8px rgba(255,255,100,0.9))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    pointerEvents: 'auto',
                                    touchAction: 'none',
                                }} />
                        );
                    })}

                    {/* Ghost piece following cursor during drag */}
                    {dragSq && dragPos && pieceMap[dragSq] && (() => {
                        return (
                            <img src={PIECES[pieceMap[dragSq]]} alt="drag"
                                draggable={false}
                                style={{position: 'absolute',
                                    left: dragPos.x - pointerDownOffsetRef.current.x,
                                    top:  dragPos.y - pointerDownOffsetRef.current.y,
                                    width: sqSize, height: sqSize,
                                    pointerEvents: 'none',
                                    zIndex: 9999,
                                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
                                    userSelect: 'none',
                                    opacity: 0.92,
                                }} />
                        );
                    })()}
                </div>

                {/* Player Profile + Timer */}
                <div className="glass-panel" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderRadius: '10px' }}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{fontSize: '28px' }}>👤</div>
                        <div>
                            <div style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '15px' }}>{displayUsername}</div>
                            <div style={{color: 'var(--text-secondary)', fontSize: '12px' }}>You • {playerColor === 'w' ? '⬜ White' : '⬛ Black'}</div>
                        </div>
                    </div>
                    <div style={{background: game.turn() === playerColor && !game.isGameOver() ? 'rgba(59,130,246,0.15)' : 'rgba(0,0,0,0.3)', padding: '6px 18px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '22px', color: game.turn() === playerColor ? '#3b82f6' : '#64748b', fontWeight: 'bold', border: game.turn() === playerColor ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent', transition: 'all 0.3s' }}>
                        {formatTime(playerTime)}
                    </div>
                </div>
            </div>

            {/* ── Notation + Controls Panel ── */}
            <div className="glass-panel" style={{flex: 1, minWidth: '260px', maxWidth: '340px', display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', height: boardW + 16 }}>

                {/* Header */}
                <div style={{background: 'rgba(0,0,0,0.3)', padding: '14px 18px', borderBottom: '1px solid var(--border-glass)', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>📝 Notation</span>
                    <button onClick={() => { navigator.clipboard.writeText(game.pgn()); toast.success('PGN Copied!'); }} style={{background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>📋 Copy PGN</button>
                </div>

                {/* Move List (Lichess-style) */}
                <div ref={moveListRef} style={{flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', alignContent: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                    {moveHistory.length === 0
                        ? <span style={{color: '#475569', fontSize: '13px', fontStyle: 'italic', margin: 'auto' }}>Make your first move...</span>
                        : moveHistory.reduce((res, v, i, arr) => { if (i % 2 === 0) res.push(arr.slice(i, i + 2)); return res; }, []).map((pair, idx) => {
                            const wIdx = idx * 2 + 1;
                            const bIdx = idx * 2 + 2;
                            return (
                                <div key={idx} style={{display: 'flex', gap: '5px', fontSize: '14px', alignItems: 'center', background: 'var(--neu-inner)', padding: '4px 8px', borderRadius: '6px', width: '100%', boxShadow: 'var(--neu-shadow-inset)' }}>
                                    <span style={{color: '#f59e0b', fontWeight: 'bold', width: '28px', flexShrink: 0 }}>{idx + 1}.</span>
                                    <span onClick={() => setViewIndex(wIdx)} style={{color: viewIndex === wIdx ? 'white' : (viewIndex >= wIdx ? 'var(--text-primary)' : '#64748b'), fontWeight: 'bold', flex: 1, cursor: 'pointer', background: viewIndex === wIdx ? '#3b82f6' : 'transparent', padding: '2px 5px', borderRadius: '4px', transition: 'all 0.15s' }}>
                                        {pair[0]}
                                    </span>
                                    {pair[1]
                                        ? <span onClick={() => setViewIndex(bIdx)} style={{color: viewIndex === bIdx ? 'white' : (viewIndex >= bIdx ? 'var(--text-primary)' : '#64748b'), fontWeight: 'bold', flex: 1, cursor: 'pointer', background: viewIndex === bIdx ? '#3b82f6' : 'transparent', padding: '2px 5px', borderRadius: '4px', transition: 'all 0.15s' }}>{pair[1]}</span>
                                        : <span style={{flex: 1 }} />
                                    }
                                </div>
                            );
                        })
                    }
                </div>

                {/* Playback Arrows */}
                <div style={{background: 'rgba(0,0,0,0.2)', padding: '10px', display: 'flex', justifyContent: 'center', gap: '12px', borderTop: '1px solid var(--border-glass)' }}>
                    <button onClick={() => setViewIndex(0)}                                     className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="Start">⏮</button>
                    <button onClick={() => setViewIndex(v => Math.max(0, v - 1))}               className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="Back">◀</button>
                    <button onClick={() => setViewIndex(v => Math.min(fenHistory.length - 1, v + 1))} className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="Forward">▶</button>
                    <button onClick={() => setViewIndex(fenHistory.length - 1)}                 className="neu-btn" style={{padding: '8px 14px', fontSize: '16px' }} title="End">⏭</button>
                </div>

                {/* Action Buttons */}
                <div style={{padding: '12px', display: 'flex', gap: '8px' }}>
                    <button onClick={handleUndo} disabled={moveHistory.length < 2 || isThinking} className="neu-btn"
                        style={{flex: 1, padding: '10px 6px', fontSize: '13px', opacity: moveHistory.length < 2 || isThinking ? 0.5 : 1 }}>
                        ↩ Undo
                    </button>
                    <button onClick={handleOfferDraw} disabled={drawOffered || game.isGameOver()} className="neu-btn"
                        style={{flex: 1, padding: '10px 6px', fontSize: '13px', color: '#f59e0b', opacity: drawOffered || game.isGameOver() ? 0.5 : 1 }}>
                        🤝 Draw
                    </button>
                    <button
                        onClick={() => {
                            playSound('notify');
                            handleGameOver('Resignation', '🏳️ You Resigned');
                        }}
                        className="neu-btn"
                        style={{flex: 1, padding: '10px 6px', fontSize: '13px', color: '#ef4444' }}
                        disabled={game.isGameOver() || !!gameOverModal}
                    >
                        🏳️ Resign
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// 👁️ MINI SPECTATE BOARD — For live monitoring grid
// ============================================================
const MiniSpectateBoard = React.memo(({ fen, pieceTheme = 'cburnett', boardTheme = 'wood' }) => {
    const temp = new Chess(); 
    try { temp.load(fen); } catch(e){}
    const rows = [8,7,6,5,4,3,2,1]; 
    const cols = ['a','b','c','d','e','f','g','h'];
    const render = {}; 
    temp.board().forEach((row, r) => row.forEach((sq, c) => { 
        if(sq) render[`${cols[c]}${rows[r]}`] = sq.color + sq.type.toUpperCase(); 
    }));
    
    const PIECES = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;
    const { light, dark } = (() => {
        switch (boardTheme) {
            case 'wood':  return { light: '#f0d9b5', dark: '#b58863' };
            case 'blue':  return { light: '#dee3e6', dark: '#8ca2ad' };
            default:      return { light: '#eeeed2', dark: '#769656' };
        }
    })();

    return (
        <div style={{
            width: '100%', 
            aspectRatio: '1/1', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(8, 1fr)', 
            gridTemplateRows: 'repeat(8, 1fr)', 
            border: '1.5px solid rgba(163, 177, 198, 0.5)', 
            borderRadius: '8px', 
            overflow: 'hidden', 
            boxShadow: 'var(--neu-shadow-inset)' 
        }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const id = `${col}${row}`; 
                const isDark = (rIdx + cIdx) % 2 === 1;
                const pieceKey = render[id];
                return ( 
                    <div key={id} style={{
                        background: isDark ? dark : light, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        position: 'relative'
                    }}> 
                        {pieceKey && PIECES[pieceKey] && (
                            <img src={PIECES[pieceKey]} alt="piece" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
                        )} 
                    </div> 
                );
            }))}
        </div>
    );
});

// ============================================================
// 👁️ ACTIVE GAMES GRID — Streams all playing games in real-time
// ============================================================
const ActiveGamesGrid = ({ boardTheme, pieceTheme, onSelectGame }) => {
    const [liveGames, setLiveGames] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(
            collection(db, 'games'), 
            where('status', '==', 'playing')
        );

        const unsub = onSnapshot(q, (snap) => {
            const list = [];
            snap.forEach((docSnap) => {
                list.push({ gameId: docSnap.id, ...docSnap.data() });
            });
            list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            setLiveGames(list);
            setLoading(false);
        });

        return () => unsub();
    }, []);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                <h3>🔍 Scanning Arena for live matches...</h3>
            </div>
        );
    }

    if (liveGames.length === 0) {
        return (
            <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', borderRadius: '18px', border: '2px dashed rgba(163, 177, 198, 0.6)' }}>
                <div style={{ fontSize: '50px', marginBottom: '10px' }}>😴</div>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 8px 0' }}>No Active Matches</h3>
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    Nobody is playing in the arena right now. When a user challenges a friend or plays against a bot, the live board will appear here.
                </p>
            </div>
        );
    }

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {liveGames.map((g) => {
                const isBot = g.format === 'bot';
                return (
                    <div key={g.gameId} className="glass-panel" style={{ padding: '16px', borderRadius: '18px', display: 'flex', flexDirection: 'column', gap: '12px', border: '2px solid rgba(255,255,255,0.4)', boxShadow: 'var(--panel-shadow)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '8px' }}>
                            <div>
                                <span style={{ fontWeight: '900', color: 'var(--text-primary)', fontSize: '14px' }}>
                                    {g.whiteName || 'White'}
                                </span>
                                <span style={{ color: 'var(--text-secondary)', margin: '0 6px', fontSize: '12px' }}>vs</span>
                                <span style={{ fontWeight: '900', color: 'var(--text-primary)', fontSize: '14px' }}>
                                    {g.blackName || 'Black'}
                                </span>
                            </div>
                            <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '12px', background: isBot ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)', color: isBot ? '#10b981' : '#6366f1', fontWeight: 'bold' }}>
                                {isBot ? '🤖 Bot' : '⚔️ PvP'}
                            </span>
                        </div>

                        <div style={{ width: '100%', maxWidth: '240px', margin: '0 auto' }}>
                            <MiniSpectateBoard fen={g.fen} boardTheme={boardTheme} pieceTheme={pieceTheme} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                                <span>Format: <strong>{g.format || 'blitz'}</strong></span>
                                <span>Turn: <strong>{g.turn === 'w' ? 'White' : 'Black'}</strong></span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(g.pgn || '');
                                        toast.success('📋 Game PGN Copied!');
                                    }}
                                    className="neu-btn"
                                    style={{ flex: 1, padding: '8px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                >
                                    📋 Copy PGN
                                </button>
                                <button 
                                    onClick={() => onSelectGame({ gameId: g.gameId, color: 'w', botName: g.blackName, format: g.format, isSpectator: true })}
                                    className="neu-btn"
                                    style={{ flex: 1, padding: '8px', fontSize: '12px', fontWeight: 'bold', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                                >
                                    🔎 Watch
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ============================================================
// ⚔️ PLAYING ZONE — Main Page Component
// ============================================================
export default function Arena({ user = { email: 'guest@hayaa.com', name: 'Player' }, role }) {
    const [startFen, setStartFen] = useState(() => {
        return localStorage.getItem('arenaStartFen') || null;
    });
    const [mode, setMode]                       = useState(() => {
        const hasStartFen = localStorage.getItem('arenaStartFen');
        return hasStartFen ? 'BOT_SELECT' : 'HOME';
    });
    const [botLevel, setBotLevel]               = useState(1);
    const [selectedBotConfig, setSelectedBotConfig] = useState(null);
    const [selectedTime, setSelectedTime]       = useState(600);
    const [selectedColor, setSelectedColor]     = useState('w');
    const [resolvedPlayerColor, setResolvedPlayerColor] = useState('w'); // fixed once game starts
    const [boardTheme, setBoardTheme]           = useState('green');
    const [pieceTheme, setPieceTheme]           = useState('cburnett');
    const [userRole, setUserRole]               = useState(role || 'STUDENT');
    const [selectedSpectateMatch, setSelectedSpectateMatch] = useState(null);

    // Friends / matchmaking
    const [onlineFriends, setOnlineFriends]     = useState([]);
    const [friendSearch, setFriendSearch]       = useState('');
    const [matchFormat, setMatchFormat]         = useState('blitz_5_0');
    const [matchColor, setMatchColor]           = useState('random');
    const [isSearching, setIsSearching]         = useState(false);
    const [searchFormat, setSearchFormat]       = useState('');
    const [activeMatch, setActiveMatch]         = useState(null);
    const [activeMatchmakingDocId, setActiveMatchmakingDocId] = useState(null);

    const timeOptions = [
        { label: '1+0',   desc: 'Bullet',     secs: 60   },
        { label: '2+1',   desc: 'Bullet',     secs: 120  },
        { label: '3+0',   desc: 'Blitz',      secs: 180  },
        { label: '5+0',   desc: 'Blitz',      secs: 300  },
        { label: '10+0',  desc: 'Rapid',      secs: 600  },
        { label: '15+10', desc: 'Rapid',      secs: 900  },
        { label: '30+0',  desc: 'Classical',  secs: 1800 },
    ];

    const pairingOptions = [
        { name: '1 + 0',  type: 'Bullet',    id: 'bullet_1_0'  },
        { name: '3 + 0',  type: 'Blitz',     id: 'blitz_3_0'   },
        { name: '5 + 0',  type: 'Blitz',     id: 'blitz_5_0'   },
        { name: '10 + 0', type: 'Rapid',     id: 'rapid_10_0'  },
    ];

    // Difficulty colour per level (4 shades × 4 groups)
    const DIFF_COLORS  = ['#10b981','#10b981','#10b981','#10b981','#f59e0b','#f59e0b','#f59e0b','#f59e0b','#f97316','#f97316','#f97316','#f97316','#ef4444','#ef4444','#ef4444','#dc2626'];
    const DIFF_LABELS  = ['Baby','Baby','Easy','Easy','Beginner','Beginner','Medium','Medium','Intermediate','Intermediate','Hard','Hard','Expert','Expert','Master','Grandmaster'];

    // ── Load user preferences ──
    useEffect(() => {
        if (!user?.email || user.email === 'guest@hayaa.com') return;
        const unsub = onSnapshot(doc(db, 'users', user.email.toLowerCase()), (snap) => {
            if (snap.exists()) {
                const d = snap.data();
                if (d.boardTheme) setBoardTheme(d.boardTheme);
                if (d.pieceTheme) setPieceTheme(d.pieceTheme);
                if (d.role) setUserRole(d.role);
            }
        });
        return () => unsub();
    }, [user]);

    // ── Load online friends ──
    useEffect(() => {
        if (!user?.email || mode !== 'FRIENDS') return;
        const q = query(collection(db, 'users'), where('online', '==', true));
        getDocs(q).then(snap => {
            const friends = [];
            snap.forEach(d => {
                if (d.id !== user.email.toLowerCase()) {
                    friends.push({ id: d.id, ...d.data() });
                }
            });
            setOnlineFriends(friends);
        }).catch(() => {});
    }, [user, mode]);

    // ── Matchmaking ──
    const handlePairing = async (format) => {
        setIsSearching(true);
        setSearchFormat(format.name);
        const playerEmail = user.email.toLowerCase();
        const playerName  = user.name || playerEmail.split('@')[0];

        try {
            // Find an active matchmaking document for this format that is NOT created by the current user
            const q = query(
                collection(db, 'games'),
                where('status', '==', 'matchmaking_waiting'),
                where('format', '==', format.id)
            );
            const snap = await getDocs(q);
            
            // Find a valid waiting game from another player
            let foundMatch = null;
            for (let docSnap of snap.docs) {
                if (docSnap.data().challenger !== playerEmail) {
                    foundMatch = docSnap;
                    break;
                }
            }

            if (foundMatch) {
                const opponent = foundMatch.data();
                const gameId   = foundMatch.id;
                
                // Decide color: if opponent colorPreference is 'white', opponent gets white, player gets black
                let myColor = 'b';
                if (opponent.colorPreference === 'black') {
                    myColor = 'w';
                } else if (opponent.colorPreference === 'random') {
                    myColor = Math.random() > 0.5 ? 'w' : 'b';
                }

                const parts = format.id.split('_');
                const mins = parseInt(parts[1]) || 5;
                const initialTime = mins * 60;

                await updateDoc(doc(db, 'games', gameId), {
                    white: myColor === 'w' ? playerEmail : opponent.challenger,
                    whiteName: myColor === 'w' ? playerName : opponent.challengerName,
                    black: myColor === 'b' ? playerEmail : opponent.challenger,
                    blackName: myColor === 'b' ? playerName : opponent.challengerName,
                    status: 'playing',
                    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
                    turn: 'w',
                    matchedAt: new Date().toISOString(),
                    moves: [],
                    whiteTime: initialTime,
                    blackTime: initialTime,
                    lastMoveAt: new Date().toISOString()
                });
                
                toast.success(`Matched with ${opponent.challengerName}! ⚔️`);
                setIsSearching(false);
                setActiveMatch({ type: 'HUMAN', gameId, color: myColor, botName: opponent.challengerName, format: format.id });
            } else {
                // No waiting opponent found. Create our own waiting game document
                const gameId = `match_${format.id}_${playerEmail.replace(/[^a-zA-Z0-9]/g, '')}`;
                await setDoc(doc(db, 'games', gameId), {
                    white: playerEmail,
                    black: 'waiting',
                    challenger: playerEmail,
                    challengerName: playerName,
                    format: format.id,
                    colorPreference: 'random', // Auto-match is random color
                    status: 'matchmaking_waiting',
                    createdAt: new Date().toISOString()
                });

                setActiveMatchmakingDocId(gameId);

                const loadingToast = toast.loading(`Waiting for opponent in ${format.name}...`);
                
                const unsub = onSnapshot(doc(db, 'games', gameId), (docSnap) => {
                    if (docSnap.exists() && docSnap.data().status === 'playing') {
                        const data = docSnap.data();
                        toast.success('Opponent found! ⚔️', { id: loadingToast });
                        setIsSearching(false);
                        const myColor = data.white === playerEmail ? 'w' : 'b';
                        const oppName = myColor === 'w' ? data.blackName : data.whiteName;
                        setActiveMatch({ type: 'HUMAN', gameId, color: myColor, botName: oppName, format: format.id });
                        setActiveMatchmakingDocId(null);
                        unsub();
                    }
                });
            }
        } catch (err) {
            console.error('Matchmaking error:', err);
            toast.error('Matchmaking failed. Try again.');
            setIsSearching(false);
        }
    };

    const cancelSearch = async () => {
        if (activeMatchmakingDocId) {
            await deleteDoc(doc(db, 'games', activeMatchmakingDocId)).catch(() => {});
            setActiveMatchmakingDocId(null);
        }
        setIsSearching(false);
        toast.dismiss();
    };

    // ── Incoming invite listener (watches games collection) ──
    useEffect(() => {
        if (!user?.email || user.email === 'guest@hayaa.com') return;
        const myEmail = user.email.toLowerCase();
        const invitesQuery = query(
            collection(db, 'games'),
            where('recipient', '==', myEmail),
            where('status', '==', 'invited')
        );
        let shownToastId = null;

        const unsub = onSnapshot(invitesQuery, (snap) => {
            if (snap.empty) {
                if (shownToastId) {
                    toast.dismiss(shownToastId);
                    shownToastId = null;
                }
                return;
            }
            // Just take the first active invite
            const docChange = snap.docs[0];
            const inv = docChange.data();
            const gameId = docChange.id;

            if (shownToastId) return; // Already showing an invite
            const toastId = `invite_${gameId}`;
            shownToastId = toastId;

            toast(
                (t) => (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '240px' }}>
                        <div style={{fontWeight: 'bold', fontSize: '15px' }}>⚔️ {inv.challengerName} wants to play!</div>
                        <div style={{color: 'var(--text-secondary)', fontSize: '13px' }}>Format: {inv.format} • Color: {inv.colorPreference}</div>
                        <div style={{display: 'flex', gap: '8px' }}>
                            <button onClick={async () => {
                                toast.dismiss(t.id);
                                shownToastId = null;
                                const myColor = inv.colorPreference === 'white' ? 'b' : (inv.colorPreference === 'black' ? 'w' : (Math.random() > 0.5 ? 'w' : 'b'));
                                const parts = (inv.format || 'blitz_5_0').split('_');
                                const mins = parseInt(parts[1]) || 5;
                                const initialTime = mins * 60;

                                await updateDoc(doc(db, 'games', gameId), {
                                    white: myColor === 'w' ? myEmail : inv.challenger,
                                    whiteName: myColor === 'w' ? (user.name || myEmail.split('@')[0]) : inv.challengerName,
                                    black: myColor === 'b' ? myEmail : inv.challenger,
                                    blackName: myColor === 'b' ? (user.name || myEmail.split('@')[0]) : inv.challengerName,
                                    fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
                                    turn: 'w',
                                    status: 'playing',
                                    acceptedAt: new Date().toISOString(),
                                    moves: [],
                                    whiteTime: initialTime,
                                    blackTime: initialTime,
                                    lastMoveAt: new Date().toISOString()
                                });
                                setActiveMatch({ type: 'HUMAN', gameId, color: myColor, botName: inv.challengerName });
                            }} style={{flex: 1, padding: '8px', background: '#10b981', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Accept</button>
                            <button onClick={async () => {
                                toast.dismiss(t.id);
                                shownToastId = null;
                                await updateDoc(doc(db, 'games', gameId), { status: 'declined' });
                            }} style={{flex: 1, padding: '8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>Decline</button>
                        </div>
                    </div>
                ),
                { id: toastId, duration: 60000 }
            );
        });
        return () => unsub();
    }, [user]);

    // ── Send invite: write directly to games collection (since it's a allowed collection) ──
    const inviteFriend = async (friend) => {
        const challengerEmail = user.email.toLowerCase();
        const challengerName = user.name || challengerEmail.split('@')[0];
        const recipientEmail = friend.id;
        const recipientName = friend.name || recipientEmail.split('@')[0];
        const gameId = `challenge_${Date.now()}_${challengerEmail.replace(/[^a-zA-Z0-9]/g, '')}`;

        const loadingToast = toast.loading(`Sending challenge to ${recipientName}...`);

        try {
            await setDoc(doc(db, 'games', gameId), {
                white: matchColor === 'black' ? recipientEmail : challengerEmail,
                whiteName: matchColor === 'black' ? recipientName : challengerName,
                black: matchColor === 'black' ? challengerEmail : recipientEmail,
                blackName: matchColor === 'black' ? challengerName : recipientName,
                challenger: challengerEmail,
                challengerName: challengerName,
                recipient: recipientEmail,
                recipientName: recipientName,
                format: matchFormat,
                colorPreference: matchColor,
                status: 'invited',
                createdAt: new Date().toISOString()
            });

            toast.success(`📩 Challenge sent! Waiting for response...`, { id: loadingToast });

            // Listen for acceptance/decline
            const unsub = onSnapshot(doc(db, 'games', gameId), (snap) => {
                if (!snap.exists()) return;
                const data = snap.data();
                if (data.status === 'playing') {
                    toast.success(`${recipientName} accepted the challenge! ⚔️`);
                    const myColor = data.white === challengerEmail ? 'w' : 'b';
                    setActiveMatch({ type: 'HUMAN', gameId, color: myColor, botName: recipientName });
                    unsub();
                } else if (data.status === 'declined') {
                    toast.error(`${recipientName} declined the challenge.`);
                    unsub();
                }
            });
        } catch (e) {
            console.error('Invite error:', e);
            toast.error(`Could not send challenge: ${e.code || e.message}`, { id: loadingToast });
        }
    };

    // ── RENDER: Active live match ──
    if (activeMatch) {
        return (
            <div style={{paddingTop: '16px' }}>
                <ArenaBoard matchConfig={activeMatch} user={user} exitMatch={() => setActiveMatch(null)} boardTheme={boardTheme} pieceTheme={pieceTheme} />
            </div>
        );
    }

    // ── RENDER: Active bot game ──
    if (mode === 'PLAYING_BOT') {
        return (
            <div style={{width: '100%', paddingBottom: '20px' }}>
                <button onClick={() => setMode('BOT_SETUP')} className="neu-btn" style={{marginBottom: '14px', marginLeft: '24px', padding: '10px 22px' }}>
                    ◀ Change Setup
                </button>
                <BotBoard
                    key={`${botLevel}-${selectedTime}-${resolvedPlayerColor}`}
                    level={botLevel}
                    boardTheme={boardTheme}
                    pieceTheme={pieceTheme}
                    user={user}
                    playerColor={resolvedPlayerColor}
                    timeControl={selectedTime}
                    onExit={() => {
                        setStartFen(null);
                        localStorage.removeItem('arenaStartFen');
                        setMode('HOME');
                    }}
                    startFen={startFen}
                />
            </div>
        );
    }

    // ── RENDER: Bot setup screen ──
    if (mode === 'BOT_SETUP' && selectedBotConfig) {
        const bc = selectedBotConfig;
        const em = bc.name.split(' ')[0];
        const nm = bc.name.substring(em.length).trim();
        const diffIdx = botLevel - 1;
        const diffLabel = DIFF_LABELS[diffIdx] || 'Custom';
        const diffColor = DIFF_COLORS[diffIdx] || 'var(--text-secondary)';
        return (
            <div style={{ width: '100%', maxWidth: '700px', margin: '0 auto', padding: '20px' }}>
                <button 
                    onClick={() => setMode('BOT_SELECT')} 
                    className="kid-btn-bubble" 
                    style={{ 
                        marginBottom: '24px', 
                        padding: '10px 22px', 
                        background: 'var(--bg-primary)', 
                        color: 'var(--text-primary)', 
                        border: '3px solid #fff', 
                        borderRadius: '50px', 
                        fontWeight: '900', 
                        cursor: 'pointer',
                        boxShadow: 'var(--neu-shadow)',
                        fontSize: '14px'
                    }}
                >
                    ◀ Back to Bots
                </button>
                <div className="glass-panel" style={{ padding: '40px', borderRadius: '28px', textAlign: 'center', border: '3px solid #fff', boxShadow: 'var(--panel-shadow)', background: 'var(--bg-primary)' }}>
                    <div className="animate-float-cute" style={{ fontSize: '72px', marginBottom: '8px' }}>{em}</div>
                    <h2 style={{ color: 'var(--text-primary)', margin: '0 0 6px 0', fontSize: '26px', fontWeight: '900' }}>{nm}</h2>
                    <div style={{ color: diffColor, background: `${diffColor}12`, padding: '6px 20px', borderRadius: '20px', display: 'inline-block', fontWeight: '900', fontSize: '13px', marginBottom: '32px', textTransform: 'uppercase', border: `1.5px solid ${diffColor}22` }}>
                        {diffLabel} — Level {botLevel}
                    </div>

                    {/* Time Control */}
                    <div style={{ marginBottom: '28px', textAlign: 'left' }}>
                        <div style={{ color: '#1e3a8a', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '1px' }}>⏱ Select Battle Duration</div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {timeOptions.map(t => {
                                const isSelected = selectedTime === t.secs;
                                return (
                                    <button 
                                        key={t.label} 
                                        onClick={() => setSelectedTime(t.secs)}
                                        className="kid-btn-bubble"
                                        style={{
                                            padding: '10px 16px', 
                                            background: isSelected ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'var(--bg-primary)', 
                                            color: isSelected ? '#white' : 'var(--text-primary)', 
                                            border: isSelected ? '2px solid #fff' : '2px solid rgba(163, 177, 198, 0.4)', 
                                            borderRadius: '12px', 
                                            cursor: 'pointer', 
                                            fontWeight: '900', 
                                            fontSize: '14px', 
                                            boxShadow: isSelected ? '0 4px 10px rgba(37,99,235,0.3)' : 'var(--neu-shadow)',
                                            outline: 'none'
                                        }}
                                    >
                                        <div style={{ color: isSelected ? '#fff' : 'var(--text-primary)' }}>{t.label}</div>
                                        <div style={{ fontSize: '11px', color: isSelected ? '#bfdbfe' : 'var(--text-secondary)' }}>{t.desc}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Color Picker */}
                    <div style={{ marginBottom: '32px', textAlign: 'left' }}>
                        <div style={{ color: '#1e3a8a', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '1px' }}>♟️ Select Army Side</div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {[{ val: 'w', label: '⬜ White (Go First)' }, { val: 'b', label: '⬛ Black' }, { val: 'random', label: '🎲 Random' }].map(c => {
                                const isSelected = selectedColor === c.val;
                                return (
                                    <button 
                                        key={c.val} 
                                        onClick={() => setSelectedColor(c.val)}
                                        className="kid-btn-bubble"
                                        style={{
                                            flex: 1, 
                                            padding: '14px', 
                                            background: isSelected ? 'rgba(59, 130, 246, 0.12)' : 'var(--bg-primary)', 
                                            color: 'var(--text-primary)', 
                                            border: isSelected ? '2.5px solid #3b82f6' : '2.5px solid rgba(163, 177, 198, 0.4)', 
                                            borderRadius: '16px', 
                                            cursor: 'pointer', 
                                            fontWeight: '900', 
                                            fontSize: '14px',
                                            boxShadow: isSelected ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                            outline: 'none'
                                        }}
                                    >
                                        {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <button 
                        onClick={() => {
                            const color = selectedColor === 'random'
                                ? (Math.random() > 0.5 ? 'w' : 'b')
                                : selectedColor;
                            setResolvedPlayerColor(color);
                            setMode('PLAYING_BOT');
                        }}
                        className="kid-btn-bubble animate-wiggle"
                        style={{
                            width: '100%', 
                            padding: '18px', 
                            background: 'linear-gradient(135deg, #10b981, #059669)', 
                            color: '#fff', 
                            border: '3px solid #fff', 
                            borderRadius: '50px', 
                            fontWeight: '900', 
                            fontSize: '18px', 
                            cursor: 'pointer', 
                            letterSpacing: '1px', 
                            boxShadow: '0 6px 20px rgba(16,185,129,0.4)',
                            outline: 'none'
                        }}
                    >
                        ⚔️ LAUNCH BATTLE!
                    </button>
                </div>
            </div>
        );
    }

    // ── RENDER: Bot selection grid (16 bots) ──
    if (mode === 'BOT_SELECT') {
        return (
            <div style={{ width: '100%', maxWidth: '1050px', margin: '0 auto', padding: '20px', paddingBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '35px', flexWrap: 'wrap' }}>
                    <button 
                        onClick={() => setMode('HOME')} 
                        className="kid-btn-bubble" 
                        style={{ 
                            padding: '10px 22px', 
                            background: 'var(--bg-primary)', 
                            color: 'var(--text-primary)', 
                            border: '3px solid #fff', 
                            borderRadius: '50px', 
                            fontWeight: '900', 
                            cursor: 'pointer',
                            boxShadow: 'var(--neu-shadow)',
                            fontSize: '14px'
                        }}
                    >
                        ◀ Back
                    </button>
                    <div>
                        <h2 style={{ color: '#1e3a8a', margin: 0, fontSize: '26px', fontWeight: '900' }}>🤖 Choose Your AI Sidekick</h2>
                        <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '14px', fontWeight: '700' }}>
                            Pick an opponent from Snail Bot to fearsome grandmasters and start training!
                        </p>
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '20px' }}>
                    {BOTS.map((bot, i) => {
                        const em = bot.name.split(' ')[0];
                        const nm = bot.name.substring(em.length).trim();
                        const isSelected = botLevel === i + 1;
                        const dc = DIFF_COLORS[i];
                        return (
                            <div
                                key={bot.id}
                                onClick={() => { setBotLevel(i + 1); setSelectedBotConfig(bot); setMode('BOT_SETUP'); }}
                                className="glass-panel kid-card"
                                style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    padding: '24px 16px', 
                                    cursor: 'pointer', 
                                    border: isSelected ? `3px solid ${dc}` : '3px solid rgba(255, 255, 255, 0.95)', 
                                    borderRadius: '24px', 
                                    textAlign: 'center',
                                    background: 'var(--bg-primary)',
                                    boxShadow: isSelected ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)'
                                }}
                            >
                                <div className="animate-float-cute" style={{ fontSize: '46px', marginBottom: '10px' }}>{em}</div>
                                <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: '900', marginBottom: '8px', lineHeight: 1.3 }}>{nm}</div>
                                <div style={{ 
                                    fontSize: '10px', 
                                    color: dc, 
                                    background: `${dc}12`, 
                                    padding: '4px 12px', 
                                    borderRadius: '20px', 
                                    fontWeight: '900', 
                                    textTransform: 'uppercase', 
                                    letterSpacing: '0.5px', 
                                    marginBottom: '6px',
                                    border: `1px solid ${dc}22`
                                }}>
                                    {DIFF_LABELS[i]}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Level {i + 1}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // ── RENDER: Play with Friends ──
    if (mode === 'FRIENDS') {
        const filtered = onlineFriends.filter(f =>
            !friendSearch || (f.name || f.id).toLowerCase().includes(friendSearch.toLowerCase())
        );
        return (
            <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto', padding: '20px', paddingBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                    <button 
                        onClick={() => { setMode('HOME'); setIsSearching(false); toast.dismiss(); }} 
                        className="kid-btn-bubble" 
                        style={{ 
                            padding: '10px 22px', 
                            background: 'var(--bg-primary)', 
                            color: 'var(--text-primary)', 
                            border: '3px solid #fff', 
                            borderRadius: '50px', 
                            fontWeight: '900', 
                            cursor: 'pointer',
                            boxShadow: 'var(--neu-shadow)',
                            fontSize: '14px'
                        }}
                    >
                        ◀ Back
                    </button>
                    <div>
                        <h2 style={{ color: '#1e3a8a', margin: 0, fontSize: '26px', fontWeight: '900' }}>👥 Battle with Friends</h2>
                        <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '14px', fontWeight: '700' }}>
                            <span style={{ color: '#10b981', fontWeight: '900' }}>{onlineFriends.length}</span> classmate{onlineFriends.length !== 1 ? 's' : ''} online right now
                        </p>
                    </div>
                </div>

                {/* Match Setup Bar */}
                <div className="glass-panel" style={{ padding: '25px', borderRadius: '28px', marginBottom: '25px', border: '3px solid #fff', boxShadow: 'var(--panel-shadow)', background: 'var(--bg-primary)' }}>
                    <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: '220px' }}>
                            <div style={{ color: '#1e3a8a', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '1px' }}>⚡ Select Format</div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {pairingOptions.map(p => {
                                    const isSelected = matchFormat === p.id;
                                    return (
                                        <button 
                                            key={p.id} 
                                            onClick={() => setMatchFormat(p.id)}
                                            className="kid-btn-bubble"
                                            style={{
                                                padding: '8px 14px', 
                                                background: isSelected ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'var(--bg-primary)', 
                                                color: isSelected ? '#fff' : 'var(--text-primary)', 
                                                border: isSelected ? '2px solid #fff' : '2px solid rgba(163, 177, 198, 0.4)', 
                                                borderRadius: '10px', 
                                                cursor: 'pointer', 
                                                fontWeight: '900', 
                                                fontSize: '13px',
                                                boxShadow: isSelected ? '0 4px 10px rgba(37,99,235,0.3)' : 'var(--neu-shadow)',
                                                outline: 'none'
                                            }}
                                        >
                                            {p.name} <span style={{ color: isSelected ? '#bfdbfe' : 'var(--text-secondary)', fontSize: '11px', fontWeight: 'bold' }}>{p.type}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <div style={{ color: '#1e3a8a', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '1px' }}>♟️ Select Army Side</div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {[{ val: 'white', label: '⬜' }, { val: 'black', label: '⬛' }, { val: 'random', label: '🎲' }].map(c => {
                                    const isSelected = matchColor === c.val;
                                    return (
                                        <button 
                                            key={c.val} 
                                            onClick={() => setMatchColor(c.val)}
                                            className="kid-btn-bubble"
                                            style={{
                                                padding: '8px 18px', 
                                                background: isSelected ? 'rgba(59,130,246,0.12)' : 'var(--bg-primary)', 
                                                color: 'var(--text-primary)', 
                                                border: isSelected ? '2px solid #3b82f6' : '2px solid rgba(163, 177, 198, 0.4)', 
                                                borderRadius: '10px', 
                                                cursor: 'pointer', 
                                                fontWeight: '900', 
                                                fontSize: '16px',
                                                boxShadow: isSelected ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                                outline: 'none'
                                            }}
                                        >
                                            {c.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Pairing Banner */}
                <div className="glass-panel" style={{ padding: '20px 25px', borderRadius: '24px', marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', border: '3px solid #6366f1', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)' }}>
                    <div>
                        <div style={{ color: '#1e3a8a', fontWeight: '900', marginBottom: '3px', fontSize: '16px' }}>🌐 Auto-Matchmaker</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold' }}>Match with any active student instantly!</div>
                    </div>
                    {isSearching ? (
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <span style={{ color: '#f59e0b', fontWeight: '900', fontSize: '14px', animation: 'pulse 1s infinite' }}>⏳ Tuning Radar...</span>
                            <button 
                                onClick={cancelSearch} 
                                className="kid-btn-bubble" 
                                style={{ 
                                    color: '#fff', 
                                    padding: '8px 20px', 
                                    background: '#ef4444', 
                                    border: '2px solid #fff', 
                                    borderRadius: '50px', 
                                    cursor: 'pointer', 
                                    fontWeight: '900', 
                                    boxShadow: '0 4px 10px rgba(239,68,68,0.3)',
                                    fontSize: '13px'
                                }}
                            >
                                ✖ Cancel Search
                            </button>
                        </div>
                    ) : (
                        <button 
                            onClick={() => handlePairing(pairingOptions.find(o => o.id === matchFormat) || pairingOptions[2])}
                            className="kid-btn-bubble animate-wiggle"
                            style={{ 
                                padding: '12px 28px', 
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', 
                                color: '#fff', 
                                border: '3px solid #fff', 
                                borderRadius: '50px', 
                                fontWeight: '900', 
                                cursor: 'pointer', 
                                fontSize: '14px', 
                                boxShadow: '0 5px 15px rgba(99,102,241,0.4)',
                                outline: 'none'
                            }}
                        >
                            🔍 FIND OPPONENT NOW!
                        </button>
                    )}
                </div>

                {/* Search Bar */}
                <div style={{ marginBottom: '20px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: '16px', zIndex: 5 }}>🔍</span>
                    <input
                        placeholder="Search classmates by name or ID..."
                        value={friendSearch}
                        onChange={e => setFriendSearch(e.target.value)}
                        style={{ 
                            width: '100%', 
                            padding: '14px 16px 14px 46px', 
                            background: 'var(--bg-primary)', 
                            border: '1.5px solid rgba(163, 177, 198, 0.4)', 
                            borderRadius: '16px', 
                            color: 'var(--text-primary)', 
                            fontSize: '14px', 
                            boxSizing: 'border-box', 
                            outline: 'none', 
                            boxShadow: 'var(--neu-shadow-inset)',
                            fontWeight: '600'
                        }}
                    />
                </div>

                {/* Friends List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {filtered.length === 0 ? (
                        <div className="glass-panel" style={{ padding: '50px', textAlign: 'center', border: '3px solid rgba(255,255,255,0.95)', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)', borderRadius: '28px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '14px' }}>😴</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '15px', fontWeight: '800' }}>
                                {friendSearch ? 'No classmates matching your search!' : 'No other classmates online right now. Invite them later!'}
                            </div>
                        </div>
                    ) : (
                        filtered.map(friend => (
                            <div 
                                key={friend.id} 
                                className="glass-panel" 
                                style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    padding: '16px 25px', 
                                    borderRadius: '24px', 
                                    border: '3px solid rgba(255, 255, 255, 0.95)',
                                    background: 'var(--bg-primary)',
                                    boxShadow: 'var(--panel-shadow)'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <div className="animate-float-cute" style={{ 
                                        width: '46px', 
                                        height: '46px', 
                                        background: 'var(--bg-primary)', 
                                        borderRadius: '50%', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontSize: '22px', 
                                        border: '2.5px solid #fff', 
                                        boxShadow: 'var(--neu-shadow)',
                                        flexShrink: 0 
                                    }}>
                                        {friend.avatar || '👤'}
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-primary)', fontWeight: '900', fontSize: '16px' }}>{friend.name || friend.id}</div>
                                        <div style={{ color: '#10b981', fontSize: '12px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <span style={{ width: '8px', height: '8px', background: '#10b981', borderRadius: '50%', display: 'inline-block' }} />
                                            Active Questing
                                        </div>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => inviteFriend(friend)}
                                    className="kid-btn-bubble"
                                    style={{ 
                                        padding: '10px 22px', 
                                        background: 'linear-gradient(135deg, #10b981, #059669)', 
                                        color: '#fff', 
                                        border: '2px solid #fff', 
                                        borderRadius: '50px', 
                                        fontWeight: '900', 
                                        cursor: 'pointer', 
                                        fontSize: '13px',
                                        boxShadow: '0 4px 10px rgba(16,185,129,0.3)',
                                        outline: 'none'
                                    }}
                                >
                                    ⚔️ CHALLENGE!
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    }

    // ── RENDER: Live Spectating Zone ──
    if (mode === 'WATCH') {
        return (
            <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '20px', paddingBottom: '40px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                    <button 
                        onClick={() => { setMode('HOME'); setSelectedSpectateMatch(null); }} 
                        className="kid-btn-bubble" 
                        style={{ 
                            padding: '10px 22px', 
                            background: 'var(--bg-primary)', 
                            color: 'var(--text-primary)', 
                            border: '3px solid #fff', 
                            borderRadius: '50px', 
                            fontWeight: '900', 
                            cursor: 'pointer',
                            boxShadow: 'var(--neu-shadow)',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        ◀ Back
                    </button>
                    <div>
                        <h2 style={{ color: '#1e3a8a', margin: 0, fontSize: '26px', fontWeight: '900' }}>👁️ Live Spectating Zone</h2>
                        <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '14px', fontWeight: '700' }}>
                            Watch ongoing arena matches in real-time and copy FENs/PGNs.
                        </p>
                    </div>
                </div>

                {selectedSpectateMatch ? (
                    <div>
                        <div style={{ marginBottom: '20px' }}>
                            <button 
                                onClick={() => setSelectedSpectateMatch(null)}
                                className="neu-btn"
                                style={{ padding: '8px 16px', fontWeight: 'bold' }}
                            >
                                ⬅ Back to Live List
                            </button>
                        </div>
                        <ArenaBoard 
                            matchConfig={selectedSpectateMatch} 
                            user={user} 
                            exitMatch={() => setSelectedSpectateMatch(null)} 
                            boardTheme={boardTheme} 
                            pieceTheme={pieceTheme} 
                        />
                    </div>
                ) : (
                    <ActiveGamesGrid 
                        boardTheme={boardTheme} 
                        pieceTheme={pieceTheme} 
                        onSelectGame={(gameConfig) => setSelectedSpectateMatch(gameConfig)} 
                    />
                )}
            </div>
        );
    }

    // ── RENDER: Home Screen ──
    return (
        <div style={{ width: '100%', maxWidth: '1000px', margin: '0 auto', padding: '20px', paddingBottom: '40px' }}>
            <div className="glass-panel" style={{ 
                padding: '25px', 
                marginBottom: '35px', 
                textAlign: 'center', 
                border: '3px solid #fff',
                boxShadow: 'var(--panel-shadow)',
                background: 'var(--bg-primary)',
                borderRadius: '28px'
            }}>
                <h1 style={{ color: '#1e3a8a', margin: '0 0 10px 0', fontSize: '30px', fontWeight: '900' }}>
                    ⚔️ CHESS BATTLE ARENA ⚔️
                </h1>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '15px', fontWeight: '700' }}>
                    Challenge a classmate to a live duel or practice your moves against our AI sidekicks!
                </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
                
                {/* Play with Friends Card */}
                <div
                    onClick={() => setMode('FRIENDS')}
                    className="glass-panel kid-card"
                    style={{ 
                        padding: '40px 30px', 
                        cursor: 'pointer', 
                        textAlign: 'center', 
                        borderRadius: '28px', 
                        border: '3px solid #6366f1', 
                        position: 'relative', 
                        overflow: 'hidden',
                        background: 'var(--bg-primary)',
                        boxShadow: 'var(--panel-shadow)'
                    }}
                >
                    <div className="animate-float-cute" style={{ fontSize: '70px', marginBottom: '15px' }}>👥</div>
                    <h3 style={{ color: '#1e3a8a', fontSize: '22px', fontWeight: '900', margin: '0 0 10px 0' }}>Play with Friends</h3>
                    <p style={{ color: 'var(--text-secondary)', margin: '0 0 25px 0', fontSize: '14px', fontWeight: '700', lineHeight: '1.5' }}>
                        Challenge other students who are online! Send them a challenge invite and play in real-time.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        {['⚡ Bullet', '🔥 Blitz', '⏱ Rapid'].map(f => (
                            <span key={f} style={{ 
                                padding: '4px 12px', 
                                background: 'rgba(99, 102, 241, 0.08)', 
                                color: '#6366f1', 
                                borderRadius: '20px', 
                                fontSize: '11px', 
                                fontWeight: '900',
                                border: '1px solid rgba(99, 102, 241, 0.2)',
                                boxShadow: 'var(--neu-shadow-inset)'
                            }}>
                                {f}
                            </span>
                        ))}
                    </div>
                    {/* Online badge */}
                    <div style={{ 
                        position: 'absolute', 
                        top: '16px', 
                        right: '16px', 
                        background: 'linear-gradient(135deg, #10b981, #059669)', 
                        borderRadius: '20px', 
                        padding: '4px 12px', 
                        fontSize: '11px', 
                        color: '#fff', 
                        fontWeight: '900',
                        border: '1.5px solid #fff',
                        boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
                    }}>
                        🟢 {onlineFriends.length} Online
                    </div>
                </div>

                {/* Play with Computer Card */}
                <div
                    onClick={() => setMode('BOT_SELECT')}
                    className="glass-panel kid-card"
                    style={{ 
                        padding: '40px 30px', 
                        cursor: 'pointer', 
                        textAlign: 'center', 
                        borderRadius: '28px', 
                        border: '3px solid #10b981', 
                        position: 'relative', 
                        overflow: 'hidden',
                        background: 'var(--bg-primary)',
                        boxShadow: 'var(--panel-shadow)'
                    }}
                >
                    <div className="animate-float-cute" style={{ fontSize: '70px', marginBottom: '15px' }}>🤖</div>
                    <h3 style={{ color: '#1e3a8a', fontSize: '22px', fontWeight: '900', margin: '0 0 10px 0' }}>Play with Computer</h3>
                    <p style={{ color: 'var(--text-secondary)', margin: '0 0 25px 0', fontSize: '14px', fontWeight: '700', lineHeight: '1.5' }}>
                        Practice against our smart AI sidekicks. From Snail Bot 🐌 to fearsome Master Bots!
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {BOTS.slice(0, 5).map((b, i) => (
                            <span key={i} style={{ 
                                padding: '4px 10px', 
                                background: 'rgba(16, 185, 129, 0.08)', 
                                color: '#10b981', 
                                borderRadius: '20px', 
                                fontSize: '12px', 
                                fontWeight: '900',
                                border: '1px solid rgba(16, 185, 129, 0.2)',
                                boxShadow: 'var(--neu-shadow-inset)'
                            }}>
                                {b.name.split(' ')[0]}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Spectate Games Card (Only for non-students) */}
                {(userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'COACH' || userRole === 'CHIEF_COACH') && (
                    <div
                        onClick={() => setMode('WATCH')}
                        className="glass-panel kid-card"
                        style={{ 
                            padding: '40px 30px', 
                            cursor: 'pointer', 
                            textAlign: 'center', 
                            borderRadius: '28px', 
                            border: '3px solid #f59e0b', 
                            position: 'relative', 
                            overflow: 'hidden',
                            background: 'var(--bg-primary)',
                            boxShadow: 'var(--panel-shadow)'
                        }}
                    >
                        <div className="animate-float-cute" style={{ fontSize: '70px', marginBottom: '15px' }}>👁️</div>
                        <h3 style={{ color: '#1e3a8a', fontSize: '22px', fontWeight: '900', margin: '0 0 10px 0' }}>Spectate Games</h3>
                        <p style={{ color: 'var(--text-secondary)', margin: '0 0 25px 0', fontSize: '14px', fontWeight: '700', lineHeight: '1.5' }}>
                            Monitor ongoing arena matches in real-time. Review moves, copy game PGNs, and teach!
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ 
                                padding: '4px 12px', 
                                background: 'rgba(245, 158, 11, 0.08)', 
                                color: '#f59e0b', 
                                borderRadius: '20px', 
                                fontSize: '11px', 
                                fontWeight: '900',
                                border: '1px solid rgba(245, 158, 11, 0.2)',
                                boxShadow: 'var(--neu-shadow-inset)'
                            }}>
                                📺 Live Monitor
                            </span>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
